import { Inject, Injectable } from '@angular/core';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  GetManyV2Dto,
  GetOneDto,
  PageableListV3Dto,
  ResponseDto,
  SimpleResponseDto,
} from '@fleet-beat/core';
import { catchError, map, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AddChannelModel,
  CameraModelDto,
  CameraParamsModel,
  CameraVendorDto,
  CreateDashcamModel,
  CreateEventVideoBundleModel,
  CreateLiveStreamModel,
  CreatePlaybackSessionModel,
  DashcamDto,
  EditDashcamModel,
  LiveStreamSessionModel,
  PlaybackSessionModel,
  SafetyEventModelV2,
  SafetyEventNeighborsModel,
  SafetyEventVideoBundle,
  SafetyMediaModel,
} from '@fleet-beat/api';

@Injectable({
  providedIn: 'root',
})
export class SafetyApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/cameras`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(params: GetManyV2Dto, auth: AuthHeaders): Observable<PageableListV3Dto<DashcamDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DashcamDto[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getOne(id: number, auth: AuthHeaders, params?: GetOneDto): Observable<DashcamDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DashcamDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  createOne(data: CreateDashcamModel, auth: AuthHeaders): Observable<DashcamDto> {
    return this.baseApiService.post(this.baseUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DashcamDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  delete(id: number, auth: AuthHeaders): Observable<DashcamDto> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DashcamDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  patchOne(id: number, data: EditDashcamModel, auth: AuthHeaders): Observable<DashcamDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DashcamDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  assignToVehicle(id: number, data: { truckId: string }, auth: AuthHeaders): Observable<DashcamDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}/assignment`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DashcamDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  setChannels(id: number, data: { channels: AddChannelModel[] }, auth: AuthHeaders): Observable<DashcamDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}/set-channels`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DashcamDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  // NOTE | Assets

  getSafetyEventTypesList(auth: AuthHeaders): Observable<PageableListV3Dto<CameraVendorDto>> {
    // NOTE | DUMMY
    return this.baseApiService.get(`${this.baseUrl}/event-types`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CameraVendorDto[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getCamerasVendorList(auth: AuthHeaders): Observable<PageableListV3Dto<CameraVendorDto>> {
    return this.baseApiService.get(`${this.baseUrl}/vendors`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CameraVendorDto[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getCamerasModelsList(auth: AuthHeaders): Observable<PageableListV3Dto<CameraModelDto>> {
    return this.baseApiService.get(`${this.baseUrl}/models`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CameraModelDto[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  // NOTE | Live stream sessions

  createLiveStream(
    cameraId: number,
    data: CreateLiveStreamModel,
    auth: AuthHeaders,
  ): Observable<LiveStreamSessionModel> {
    return this.baseApiService.post(`${this.baseUrl}/${cameraId}/live-streams`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<LiveStreamSessionModel>) => this.baseApiService.mapResponse(response)),
    );
  }

  keepViewerLeaseAlive(cameraId: number, viewerId: number, auth: AuthHeaders): Observable<LiveStreamSessionModel> {
    return this.baseApiService
      .post(`${this.baseUrl}/${cameraId}/live-streams/${viewerId}/keepalive`, {}, { auth })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<LiveStreamSessionModel>) => this.baseApiService.mapResponse(response)),
      );
  }

  getStreamById(cameraId: number, streamId: number, auth: AuthHeaders): Observable<LiveStreamSessionModel> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/live-streams/${streamId}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<LiveStreamSessionModel>) => this.baseApiService.mapResponse(response)),
    );
  }

  releaseLiveStream(cameraId: number, viewerId: number, auth: AuthHeaders): Observable<SimpleResponseDto> {
    return this.baseApiService
      .delete(`${this.baseUrl}/${cameraId}/live-streams/${viewerId}`, { auth })
      .pipe(catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)));
  }

  // NOTE | PLAYBACK SESSIONS

  createPlaybackSession(
    cameraId: number,
    data: CreatePlaybackSessionModel,
    auth: AuthHeaders,
  ): Observable<PlaybackSessionModel> {
    return this.baseApiService.post(`${this.baseUrl}/${cameraId}/playback-sessions`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<PlaybackSessionModel>) => this.baseApiService.mapResponse(response)),
    );
  }

  getPlaybackSessionById(cameraId: number, sessionId: number, auth: AuthHeaders): Observable<PlaybackSessionModel> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/playback-sessions/${sessionId}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<PlaybackSessionModel>) => this.baseApiService.mapResponse(response)),
    );
  }

  stopPlaybackSession(cameraId: number, sessionId: number, auth: AuthHeaders): Observable<PlaybackSessionModel> {
    return this.baseApiService
      .post(`${this.baseUrl}/${cameraId}/playback-sessions/${sessionId}/control`, { action: 'stop' }, { auth })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<PlaybackSessionModel>) => this.baseApiService.mapResponse(response)),
      );
  }

  // NOTE | SAFETY EVENTS

  getEventTypes(auth: AuthHeaders): Observable<PageableListV3Dto<{ label: string; type: string }>> {
    return this.baseApiService.get(`${this.baseUrl}/safety-event-types`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<{ label: string; type: string }[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getSafetyEvents(
    cameraId: number,
    auth: AuthHeaders,
    params?: GetManyV2Dto,
  ): Observable<PageableListV3Dto<SafetyEventModelV2>> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/safety-events`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<SafetyEventModelV2[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getSafetyEventById(
    cameraId: number,
    eventId: number,
    auth: AuthHeaders,
    params?: GetManyV2Dto,
  ): Observable<SafetyEventModelV2> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/safety-events/${eventId}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<SafetyEventModelV2>) => this.baseApiService.mapResponse(response)),
    );
  }

  getNeighboringEvents(
    cameraId: number,
    eventId: number,
    auth: AuthHeaders,
    includeSystem: boolean = false,
  ): Observable<SafetyEventNeighborsModel> {
    return this.baseApiService
      .get(`${this.baseUrl}/${cameraId}/safety-events/${eventId}/neighbors`, { auth, params: { includeSystem } })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<SafetyEventNeighborsModel>) => this.baseApiService.mapResponse(response)),
      );
  }

  getVideoBundlesByEventId(
    cameraId: number,
    eventId: number,
    auth: AuthHeaders,
  ): Observable<PageableListV3Dto<SafetyEventVideoBundle>> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/safety-events/${eventId}/video-bundles`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<SafetyEventVideoBundle[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getVideoBundleById(cameraId: number, bundleId: number, auth: AuthHeaders): Observable<SafetyEventVideoBundle> {
    return this.baseApiService
      .get(`${this.baseUrl}/${cameraId}/safety-events/video-bundles/${bundleId}`, { auth })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<SafetyEventVideoBundle>) => this.baseApiService.mapResponse(response)),
      );
  }

  createVideoBundlesByEventId(
    cameraId: number,
    eventId: number,
    body: CreateEventVideoBundleModel,
    auth: AuthHeaders,
  ): Observable<SafetyEventVideoBundle> {
    return this.baseApiService
      .post(`${this.baseUrl}/${cameraId}/safety-events/${eventId}/video-bundles`, body, { auth })
      .pipe(
        catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
        map((response: ResponseDto<SafetyEventVideoBundle>) => this.baseApiService.mapResponse(response)),
      );
  }

  // NOTE | MEDIA API

  getDashcamMedia(
    cameraId: number,
    auth: AuthHeaders,
    params?: GetManyV2Dto,
  ): Observable<PageableListV3Dto<SafetyMediaModel>> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/media`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<SafetyMediaModel[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getDashcamMediaById(cameraId: number, eventId: number, auth: AuthHeaders): Observable<SafetyMediaModel> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/media/${eventId}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<SafetyMediaModel>) => this.baseApiService.mapResponse(response)),
    );
  }

  downloadDashcamMediaById(cameraId: number, eventId: number, auth: AuthHeaders): Observable<SafetyMediaModel> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/media/${eventId}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<SafetyMediaModel>) => this.baseApiService.mapResponse(response)),
    );
  }

  // NOTE | PARAMS

  getCameraParams(
    cameraId: number,
    auth: AuthHeaders,
    params?: GetManyV2Dto,
  ): Observable<PageableListV3Dto<CameraParamsModel>> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/locations`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CameraParamsModel[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getCameraLocation(cameraId: number, auth: AuthHeaders): Observable<CameraParamsModel | null> {
    return this.baseApiService.get(`${this.baseUrl}/${cameraId}/location`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<CameraParamsModel | null>) => this.baseApiService.mapResponse(response)),
    );
  }
}
